import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Alert, Breadcrumb, Button, Card, Container, Spinner } from "react-bootstrap";
import { Navigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnSortingTable } from "../../components/Table.js";
import {
  faDownload,
  faExternalLinkAlt,
  faLayerGroup,
  faWifi,
  faWandMagicSparkles,
  faCheckCircle,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";

export const OTHER_PROJECTS = {
  aiVision: {
    title: "AI Vision",
    icon: faWandMagicSparkles,
    accessUrl: "https://projetos.r1eventech.com.br/r1_aivision",
    description: "Ferramenta para equipes de eventos criarem imagens e vídeos com IA. Cada evento pode organizar seus templates, pedidos, resultados e créditos em um só lugar.",
    profile: [
      { label: "Execução", value: "Online" },
      { label: "Tecnologia", value: "React 19, Vite 5, Sass e bibliotecas npm" },
      { label: "Possui banco de dados próprio", value: "Sim" },
      { label: "RFID", value: "Não" },
      { label: "API própria", value: "Não" },
      { label: "Dependente da API central", value: "Sim" },
    ],
    requirements: [
      "Navegador web moderno",
      "Login válido",
    ],
  },
  experiencias: {
    title: "R1 Experiências",
    icon: faLayerGroup,
    accessUrl: "https://projetos.r1eventech.com.br/r1_experiencias/",
    description: "Plataforma web para configurar e apresentar experiências interativas em eventos. O operador cria ou abre uma personalização, escolhe o aplicativo e a fonte de participantes, configura o evento e os campos, ajusta a aparência e inicia uma sessão. As integrações com as APIs de eventos e participantes e com o serviço RFID são compartilhadas pela plataforma; antes de iniciar a apresentação, o Play valida a configuração e as conexões, e o Stop encerra a sessão.",
    applications: ["Mapa interativo", "Expositor de nomes", "Mosaico de fotos"],
    profile: [
      { label: "Execução", value: "Online" },
      { label: "Tecnologia", value: "React 19, Vite 4.5, Bootstrap 5, Axios, Framer Motion e React Router" },
      { label: "Possui banco de dados próprio", value: "Não; a biblioteca usa localStorage e os arquivos ficam no IndexedDB do navegador" },
      { label: "RFID", value: "Sim, integrado aos três aplicativos" },
      { label: "API própria", value: "Não" },
      { label: "Dependente da API central", value: "Sim, para eventos e participantes" },
    ],
    requirements: [
      "Navegador web moderno",
      "Token válido da API de eventos",
      "Acesso às APIs de eventos e participantes",
      "Evento selecionado e campos exigidos pelo aplicativo configurados",
      "Para entrada por RFID: serviço e controlador ativos, com a antena/sala confirmada e disponível",
      "Tela, TV, LED ou projetor para a apresentação",
    ],
  },
  rfidController: {
    title: "Controle RFID",
    icon: faWifi,
    accessUrl: null,
    downloadUrl: "https://projetos.r1eventech.com.br/rfid-name-display-v1.5.zip",
    downloadLabel: "Baixar versão 1.4",
    description: "Aplicativo local para conectar antenas RFID Acura Edge-30R, acompanhar leituras em tempo real e consultar o histórico salvo no computador. O agente local pode continuar funcionando sem internet e, quando configurado, também envia leituras para os serviços online.",
    profile: [
      { label: "Execução", value: "Local" },
      { label: "Tecnologia", value: "Node.js, React, Vite, Bootstrap, Socket.IO e executável Windows" },
      { label: "Possui banco de dados próprio", value: "Não" },
      { label: "RFID", value: "Sim" },
      { label: "API própria", value: "Sim" },
      { label: "Dependente da API central", value: "Sim" },
    ],
    requirements: [
      "Navegador web moderno",
      "Token do evento",
      "Windows",
      "Antena RFID Acura Edge-30R",
      "Antena na mesma rede do computador",
      "IP e porta da antena, normalmente 4001",
    ],
  },
};

const ProjectProfile = ({ profile }) => (
  <div className="row g-3">
    {profile.map((item) => (
      <div className="col-sm-6 col-lg-4" key={item.label}>
        <div className="border rounded p-3 h-100">
          <div className="text-muted small text-uppercase fw-bold mb-1">{item.label}</div>
          <div>{item.value}</div>
        </div>
      </div>
    ))}
  </div>
);

const getUserName = (user) => {
  if (user?.json && typeof user.json === "object") {
    return user.json.name || user.json.nome || user.email;
  }

  return user?.email || "Sem nome";
};

const getUserType = (type) => {
  if (Number(type) === 1) return "Administrador";
  if (Number(type) === 2) return "Super admin";
  return "Usuário";
};

const userTableColumns = [
  {
    Header: "Nome",
    accessor: "display_name",
  },
  {
    Header: "E-mail",
    accessor: "email",
  },
  {
    Header: "Senha",
    accessor: "password",
  },
  {
    Header: "Projeto",
    accessor: "project_name_display",
  },
  {
    Header: "Tipo",
    accessor: "type_display",
  },
  {
    Header: "Status",
    accessor: "status_display",
  },
  {
    Header: "Créditos",
    accessor: "credits",
  },
];

const formatUserForTable = (user) => ({
  ...user,
  display_name: getUserName(user),
  project_name_display: user.project_name || "Sem projeto",
  type_display: getUserType(user.type),
  status_display: Number(user.status) === 1 ? "Ativo" : "Inativo",
  credits: user.credits ?? 0,
});

const UsersCard = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const response = await axios.get("/r1/users", {
        params: { include_password: true },
      });
      const nextUsers = response.data?.message;

      if (!Array.isArray(nextUsers)) {
        throw new Error("Resposta inválida da lista de usuários.");
      }

      setUsers(nextUsers.map(formatUserForTable));
    } catch (requestError) {
      setError(requestError.response?.data?.message || requestError.message || "Não foi possível carregar os usuários.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  return (
    <>
      <div className="textHeaderAplicativo mt-4 mb-3 col">
        <h5 className="card-title mb-0">Lista de usuários ({users.length})</h5>
      </div>
      <h6 className="text-muted mt-3 mx-2 mb-3">Usuários cadastrados no AI Vision</h6>

      <Card>
        <Card.Body className="p-4">
          {loading && users.length === 0 ? (
            <div className="d-flex align-items-center gap-2 text-muted py-4">
              <Spinner animation="border" size="sm" />
              Carregando usuários...
            </div>
          ) : error ? (
            <Alert variant="danger" className="p-2 mb-0">{error}</Alert>
          ) : (
            <ColumnSortingTable
              columns={userTableColumns}
              title="AI Vision - Lista de usuários"
              data={users}
              removeCard
            />
          )}
        </Card.Body>
      </Card>
    </>
  );
};

const OtherProject = ({ projectKey }) => {
  const project = OTHER_PROJECTS[projectKey];

  if (!project) {
    return <Navigate to="/" replace />;
  }

  return (
    <Container className="p-0">
      <Breadcrumb>
        <Breadcrumb.Item active>{project.title}</Breadcrumb.Item>
      </Breadcrumb>
      <h6 className="text-muted mb-0">
        Projetos externos da R1 EvenTech disponíveis para acesso e operação
      </h6>

      <Card className="mt-3">
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3">
              <div
                className="rounded bg-primary text-white d-flex align-items-center justify-content-center"
                style={{ width: 52, height: 52 }}
                aria-hidden="true"
              >
                <FontAwesomeIcon icon={project.icon} size="lg" />
              </div>
              <div>
                <h4 className="mb-1">{project.title}</h4>
                <div className="text-muted small">Projeto R1 EvenTech</div>
              </div>
            </div>

            <div className="d-flex flex-wrap gap-2">
              {project.downloadUrl && (
                <Button
                  as="a"
                  href={project.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="primary"
                  className="d-inline-flex align-items-center gap-2"
                >
                  <FontAwesomeIcon icon={faDownload} />
                  {project.downloadLabel || "Baixar versão"}
                </Button>
              )}
              {project.accessUrl && (
                <Button
                  as="a"
                  href={project.accessUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="primary"
                  className="d-inline-flex align-items-center gap-2"
                >
                  <FontAwesomeIcon icon={faExternalLinkAlt} />
                  Acessar projeto
                </Button>
              )}
            </div>
            {!project.accessUrl && !project.downloadUrl && (
              <span className="text-muted small">Sem link público documentado</span>
            )}
          </div>

          {project.description && (
            <p className="text-muted mt-4 mb-4">{project.description}</p>
          )}

          {project.applications && (
            <>
              <h5 className="mb-3 mt-4">Aplicativos da plataforma</h5>
              <ul className="mb-4 ps-3">
                {project.applications.map((application) => (
                  <li className="mb-2" key={application}>{application}</li>
                ))}
              </ul>
            </>
          )}

          {project.profile && (
            <>
              <h5 className="mb-3">Informações principais</h5>
              <ProjectProfile profile={project.profile} />
            </>
          )}

          {projectKey === "aiVision" ? (
            <Alert variant="danger" className="p-2 my-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="bg-danger text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 42, height: 42 }}
                  aria-hidden="true"
                >
                  <FontAwesomeIcon icon={faTimesCircle} />
                </div>
                <div>
                  <div className="small text-uppercase fw-bold mb-1">Ajuda e suporte indisponível</div>
                  <p className="mb-0">
                    O AI Vision não possui uma seção própria de ajuda e suporte dentro da aplicação.
                  </p>
                </div>
              </div>
            </Alert>
          ) : (
            <Alert variant="success" className="p-2 my-3">
              <div className="d-flex align-items-center gap-3">
                <div
                  className="bg-success text-white rounded-circle d-flex align-items-center justify-content-center flex-shrink-0"
                  style={{ width: 42, height: 42 }}
                  aria-hidden="true"
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
                <div>
                  <div className="small text-uppercase fw-bold mb-1">Ajuda e suporte integrada</div>
                  <p className="mb-0">
                    A aplicação possui uma seção própria de ajuda, com orientações e suporte acessíveis diretamente pelo sistema.
                  </p>
                </div>
              </div>
            </Alert>
          )}

          {project.requirements && (
            <>
              <hr className="my-4" />
              <h5 className="mb-3">O que precisa</h5>
              <ul className="mb-0 ps-3">
                {project.requirements.map((requirement) => (
                  <li className="mb-2" key={requirement}>{requirement}</li>
                ))}
              </ul>
            </>
          )}

        </Card.Body>
      </Card>

      {projectKey === "aiVision" && <UsersCard />}
    </Container>
  );
};

export default OtherProject;
